const initLiveSearch = (): void => {
  const form = document.querySelector<HTMLFormElement>('[data-live-search-form]')
  const input = form?.querySelector<HTMLInputElement>('[data-live-search-input]')
  const container = document.querySelector<HTMLElement>('[data-live-search-results]')

  if (!form || !input || !container) {
    return
  }

  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  let abortController: AbortController | null = null
  let isLoading = false
  let latestRequestId = 0

  const statusElement = form.querySelector<HTMLElement>('[data-live-search-status]')

  const setLoadingState = (loading: boolean, query: string): void => {
    isLoading = loading
    form.setAttribute('aria-busy', loading ? 'true' : 'false')

    if (!statusElement) {
      return
    }

    if (loading) {
      statusElement.textContent = `Söker efter "${query}"...`
      statusElement.classList.add('animate-pulse')
    } else {
      statusElement.textContent =
        'Skriv in ett eller flera ord. Du kan söka på ord i texten, på författarnamn, kombinera ord med författarnamn etc.'
      statusElement.classList.remove('animate-pulse')
    }
  }

  const buildSearchUrl = (query: string, page: number): string => {
    const url = new URL(form.action, window.location.origin)
    const trimmed = query.trim()

    if (trimmed === '') {
      url.searchParams.delete('q')
      url.searchParams.delete('search_page')
      return url.toString()
    }

    url.searchParams.set('q', trimmed)

    if (page > 1) {
      url.searchParams.set('search_page', String(page))
    } else {
      url.searchParams.delete('search_page')
    }

    return url.toString()
  }

  const syncBrowserUrl = (query: string, page: number, mode: 'replace' | 'push'): void => {
    const nextUrl = buildSearchUrl(query, page)

    if (mode === 'replace') {
      window.history.replaceState({ q: query, page }, '', nextUrl)
    } else {
      window.history.pushState({ q: query, page }, '', nextUrl)
    }
  }

  const handleLiveSearchError = (error: unknown): void => {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return
    }

    console.error(error)
  }

  const fetchSearch = async (
    page: number,
    source: 'input' | 'pagination' | 'submit' | 'popstate'
  ): Promise<void> => {
    const q = input.value.trim()

    if (q === '') {
      container.innerHTML = ''
      setLoadingState(false, '')

      if (source !== 'popstate') {
        syncBrowserUrl('', 1, source === 'input' ? 'replace' : 'push')
      }

      return
    }

    if (abortController) {
      abortController.abort()
    }

    abortController = new AbortController()

    const requestId = ++latestRequestId
    setLoadingState(true, q)

    const ajaxUrl =
      typeof window !== 'undefined' && 'ajaxUrl' in window && typeof window.ajaxUrl === 'string'
        ? window.ajaxUrl
        : '/wp/wp-admin/admin-ajax.php'

    const url = new URL(ajaxUrl, window.location.origin)
    url.searchParams.set('action', 'eu_live_search')
    url.searchParams.set('q', q)
    url.searchParams.set('search_page', String(page))
    url.searchParams.set('page_url', form.action)

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        signal: abortController.signal
      })

      if (!response.ok) {
        throw new Error(`Live search request failed: ${response.status}`)
      }

      const json = await response.json()

      if (json?.success && json?.data?.html !== undefined) {
        container.innerHTML = json.data.html as string

        if (source === 'input') {
          syncBrowserUrl(q, page, 'replace')
        } else if (source !== 'popstate') {
          syncBrowserUrl(q, page, 'push')
        }

        if (source === 'submit' || source === 'pagination') {
          scrollToSearchTop()
        }
      }
    } finally {
      if (requestId === latestRequestId) {
        setLoadingState(false, q)
      }
    }
  }

  const scrollToSearchTop = (): void => {
    const target = document.getElementById('search-top') ?? form
    target.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  setLoadingState(false, '')

  form.addEventListener('submit', (event) => {
    event.preventDefault()

    if (isLoading) {
      return
    }

    void fetchSearch(1, 'submit').catch(handleLiveSearchError)
  })

  input.addEventListener('input', () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    debounceTimer = setTimeout(() => {
      void fetchSearch(1, 'input').catch(handleLiveSearchError)
    }, 250)
  })

  container.addEventListener('click', (event) => {
    const target = event.target as HTMLElement
    const link = target.closest<HTMLElement>('[data-live-search-page]')

    if (!link) {
      return
    }

    event.preventDefault()

    if (isLoading) {
      return
    }

    const page = Number(link.getAttribute('data-live-search-page') || '1')

    if (!Number.isNaN(page) && page > 0) {
      void fetchSearch(page, 'pagination').catch(handleLiveSearchError)
    }
  })

  window.addEventListener('popstate', () => {
    const current = new URL(window.location.href)
    const q = current.searchParams.get('q') ?? ''
    const page = Number(current.searchParams.get('search_page') ?? '1')

    input.value = q

    if (q.trim() === '') {
      container.innerHTML = ''
      setLoadingState(false, '')
      return
    }

    void fetchSearch(Number.isNaN(page) ? 1 : page, 'popstate').catch(handleLiveSearchError)
  })
}

export default initLiveSearch
